import React from "react";
import { Link } from "react-router-dom";
import DemoLayout, { demoAction } from "@/pages/DemoLayout";
import { InstructorDashboardView } from "@/pages/AdminDashboard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBranding } from "@/lib/branding";
import { Eye, Check, X, Ban, User, DollarSign, Award, TrendingUp, MessageSquarePlus, Send, ArrowRight, Palette, FileText, Calendar, ClipboardList } from "lucide-react";

const DEMO_STATS = {
  students: { total: 47, active_7d: 18, trials_active: 3 },
  certificates_issued: 12,
  intakes: { new: 6, reviewed: 4, completed: 22 },
  amendments: { new: 2, completed: 5 },
  payments: { pending: 3, completed: 24, revenue_completed: 4820 },
  appointments: { today: 2, upcoming_7d: 7, open: 5 },
  referrals: { credits_outstanding: 150 },
  recent_activity: [
    { kind: "intake",     when: new Date(Date.now() - 45 * 60 * 1000).toISOString(),        label: "Jasmine Carter submitted an intake" },
    { kind: "booking",    when: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),       label: "Marcus Wu booked a slot" },
    { kind: "amendment",  when: new Date(Date.now() - 5 * 3600 * 1000).toISOString(),       label: "Priya Nair requested a TY2023 amendment" },
    { kind: "intake",     when: new Date(Date.now() - 22 * 3600 * 1000).toISOString(),      label: "Devon Rivera submitted an intake" },
    { kind: "booking",    when: new Date(Date.now() - 26 * 3600 * 1000).toISOString(),      label: "Alicia Moreno booked a slot" },
    { kind: "intake",     when: new Date(Date.now() - 2 * 24 * 3600 * 1000).toISOString(),  label: "Terrell Banks submitted an intake" },
  ],
};

const money = (n) => Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

const PageHeader = ({ eyebrow, title, sub }) => (
  <div className="mb-8">
    <div className="label-caps mb-2">{eyebrow}</div>
    <h1 className="font-serif text-4xl lg:text-5xl leading-none mb-2">{title}</h1>
    {sub && <p className="text-muted-foreground max-w-xl">{sub}</p>}
  </div>
);

// -------- Overview --------
export function DemoOverview() {
  const { branding } = useBranding();
  return (
    <DemoLayout>
      <div className="max-w-6xl mx-auto p-6 lg:p-10">
        <InstructorDashboardView stats={DEMO_STATS} brandName={branding?.brand_name} demo />
        <div className="mt-10 rounded-md border border-primary/40 bg-primary/5 p-6 text-center">
          <div className="label-caps text-primary mb-2">Like what you see?</div>
          <h2 className="font-serif text-2xl mb-3">Try the real thing for 2 days — no card required.</h2>
          <Button asChild size="lg"><Link to="/register" data-testid="demo-footer-signup">Start free trial <ArrowRight size={14} className="ml-2" /></Link></Button>
        </div>
      </div>
    </DemoLayout>
  );
}

// -------- Branding --------
export function DemoBranding() {
  return (
    <DemoLayout>
      <div className="max-w-4xl mx-auto p-6 lg:p-10" data-testid="demo-branding">
        <PageHeader eyebrow="Branding" title="Make it yours." sub="Your logo, colors, tagline, and business info everywhere students and clients see the app." />
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-primary"><Palette size={14} /><span className="label-caps text-[10px]">Identity</span></div>
            <Field label="Brand name" v="DyNamic Tax" />
            <Field label="Tagline" v="Tax prep, done right." />
            <Field label="Logo URL" v="https://cdn.dynamicfinancialsws.online/logo.png" mono />
            <Field label="Primary color" v="#2E4FB8 (royal blue)" mono />
          </Card>
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-primary"><User size={14} /><span className="label-caps text-[10px]">Instructor profile</span></div>
            <Field label="Instructor name" v="Dimeka S." />
            <Field label="Business email" v="hello@dynamicfinancialsws.online" mono />
            <Field label="Address" v="Baltimore, MD" />
            <Field label="Welcome video" v="uploaded · 42 sec · shown on student first login" />
          </Card>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={() => demoAction("save")} data-testid="demo-branding-save">Save changes</Button>
        </div>
      </div>
    </DemoLayout>
  );
}

// -------- Students --------
const STUDENTS = [
  { name: "Jasmine Carter", email: "jasmine@example.com", progress: 92, streak: 14, plan: "Instructor", trial: false },
  { name: "Marcus Wu",      email: "marcus@example.com",  progress: 71, streak: 5,  plan: "Instructor", trial: false },
  { name: "Priya Nair",     email: "priya@example.com",   progress: 45, streak: 2,  plan: "Student",    trial: false },
  { name: "Devon Rivera",   email: "devon@example.com",   progress: 33, streak: 0,  plan: "Instructor", trial: true  },
  { name: "Alicia Moreno",  email: "alicia@example.com",  progress: 18, streak: 1,  plan: "Student",    trial: false },
  { name: "Terrell Banks",  email: "terrell@example.com", progress: 8,  streak: 0,  plan: "Student",    trial: false },
];
export function DemoStudents() {
  return (
    <DemoLayout>
      <div className="max-w-6xl mx-auto p-6 lg:p-10" data-testid="demo-students">
        <PageHeader eyebrow="Students" title="Everyone in your class." sub="Track progress, streaks, and who's on trial. Click a row to open a student's profile." />
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-left"><tr>
              <th className="p-3 label-caps text-[10px]">Name</th>
              <th className="p-3 label-caps text-[10px]">Plan</th>
              <th className="p-3 label-caps text-[10px]">Progress</th>
              <th className="p-3 label-caps text-[10px]">Streak</th>
              <th className="p-3"></th>
            </tr></thead>
            <tbody>
              {STUDENTS.map((s) => (
                <tr key={s.email} className="border-t border-border hover:bg-secondary/20 transition-colors">
                  <td className="p-3"><div>{s.name}</div><div className="text-[11px] text-muted-foreground">{s.email}</div></td>
                  <td className="p-3">{s.plan}{s.trial && <span className="ml-2 label-caps text-[9px] bg-accent/15 text-accent px-2 py-0.5 rounded">Trial</span>}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-32 h-1.5 bg-secondary rounded"><div className="h-1.5 bg-primary rounded" style={{ width: `${s.progress}%` }} /></div>
                      <span className="text-xs">{s.progress}%</span>
                    </div>
                  </td>
                  <td className="p-3">{s.streak}🔥</td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => demoAction("open")}>View</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </DemoLayout>
  );
}

// -------- Payments --------
export function DemoPayments() {
  const rows = [
    { name: "Priya Nair",    email: "priya@example.com",   method: "Cash App", amount: 199, when: "2 hours ago",  status: "submitted" },
    { name: "Devon Rivera",  email: "devon@example.com",   method: "Cash App", amount: 199, when: "1 day ago",    status: "submitted" },
    { name: "Alicia Moreno", email: "alicia@example.com",  method: "Stripe",   amount: 499, when: "3 days ago",   status: "completed" },
  ];
  return (
    <DemoLayout>
      <div className="max-w-5xl mx-auto p-6 lg:p-10" data-testid="demo-payments">
        <PageHeader eyebrow="Pending payments" title="Approve Cash App payments." sub="Stripe payments are auto-approved. Cash App submissions wait here for you to click Approve." />
        <div className="space-y-2">
          {rows.map((r, i) => (
            <Card key={i} className="p-4 flex items-center gap-4">
              <div className="flex-1"><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.email} · {r.method} · {r.when}</div></div>
              <div className="font-serif text-2xl">{money(r.amount)}</div>
              {r.status === "submitted" ? (
                <>
                  <Button size="sm" onClick={() => demoAction("approve")} data-testid={`demo-approve-${i}`}><Check size={12} className="mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => demoAction("deny")}><X size={12} className="mr-1" /> Deny</Button>
                </>
              ) : (
                <span className="label-caps text-[10px] bg-primary/15 text-primary px-2 py-1 rounded">Paid</span>
              )}
            </Card>
          ))}
        </div>
      </div>
    </DemoLayout>
  );
}

// -------- Password resets --------
export function DemoPasswordResets() {
  const rows = [
    { name: "Marcus Wu",     email: "marcus@example.com",  when: "34 minutes ago" },
    { name: "Terrell Banks", email: "terrell@example.com", when: "2 hours ago" },
  ];
  return (
    <DemoLayout>
      <div className="max-w-4xl mx-auto p-6 lg:p-10" data-testid="demo-password-resets">
        <PageHeader eyebrow="Password resets" title="Approve resets before they land." sub="Students hit Forgot password — you approve here, copy the link, and it goes out via Resend." />
        <div className="space-y-2">
          {rows.map((r, i) => (
            <Card key={i} className="p-4 flex items-center gap-4">
              <div className="flex-1"><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.email} · requested {r.when}</div></div>
              <Button size="sm" onClick={() => demoAction("approve reset")} data-testid={`demo-reset-approve-${i}`}>Approve & copy link</Button>
              <Button size="sm" variant="outline" onClick={() => demoAction("deny")}>Deny</Button>
            </Card>
          ))}
        </div>
      </div>
    </DemoLayout>
  );
}

// -------- Intake --------
const INTAKES = [
  { id: "1", name: "Jasmine Carter",   email: "jasmine@example.com", filing: "HoH",  deps: 2, files: 9,  submitted: "45 min ago", status: "new" },
  { id: "2", name: "Devon Rivera",     email: "devon@example.com",   filing: "MFJ",  deps: 3, files: 12, submitted: "22h ago",   status: "new" },
  { id: "3", name: "Marcus Wu",        email: "marcus@example.com",  filing: "Single", deps: 0, files: 5, submitted: "2 days ago", status: "reviewed" },
  { id: "4", name: "Priya Nair",       email: "priya@example.com",   filing: "MFJ",  deps: 1, files: 8,  submitted: "3 days ago", status: "completed" },
];
export function DemoIntake() {
  const [selected, setSelected] = React.useState(null);
  return (
    <DemoLayout>
      <div className="max-w-6xl mx-auto p-6 lg:p-10" data-testid="demo-intake">
        <PageHeader eyebrow="Intake forms" title="Every client return, in one queue." sub="Reveal SSN when you're ready to file (audit-logged), mark reviewed/completed, and post refund updates the client sees instantly in their portal." />
        <div className="space-y-2">
          {INTAKES.map(r => (
            <Card key={r.id} className="p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-muted-foreground">{r.email} · {r.filing} · {r.deps} dep · {r.files} files</div>
              </div>
              <div className="text-xs text-muted-foreground shrink-0">{r.submitted}</div>
              <span className={`text-[10px] label-caps px-2 py-1 rounded ${r.status === "completed" ? "bg-primary/15 text-primary" : r.status === "reviewed" ? "bg-accent/15 text-accent" : "bg-secondary text-foreground"}`}>
                {r.status === "new" ? "New" : r.status === "reviewed" ? "In progress" : "Filed"}
              </span>
              <Button size="sm" variant="outline" onClick={() => setSelected(r)}><Eye size={12} className="mr-1" /> View</Button>
            </Card>
          ))}
        </div>

        {selected && (
          <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={() => setSelected(null)}>
            <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="demo-intake-modal">
              <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
                <div>
                  <div className="label-caps text-[10px]">Intake · {selected.status}</div>
                  <div className="font-serif text-2xl">{selected.name}</div>
                  <div className="text-xs text-muted-foreground">{selected.email} · demo record</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => demoAction("reveal SSN")}><Eye size={12} className="mr-1" /> Reveal SSN</Button>
                  <Button size="sm" onClick={() => demoAction("mark complete")}><Check size={12} className="mr-1" /> Complete</Button>
                </div>
              </div>
              <div className="p-6 space-y-6 text-sm">
                <div><div className="label-caps mb-1">SSN</div><div className="font-mono">•••-••-4823</div></div>
                <div><div className="label-caps mb-1">Refund direct deposit</div><div className="font-mono">Routing 021••4021 · Acct ••••7743</div></div>
                <div>
                  <div className="label-caps mb-2">Documents</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {["W-2 (Home Depot)","1099-NEC (Uber)","1098-T (CCBC)","1095-A","Driver's license","SSN card"].map(f => <div key={f} className="p-2 border border-border rounded">📎 {f}</div>)}
                  </div>
                </div>
                <div className="border-t border-border pt-4">
                  <div className="flex items-center gap-2 mb-3 text-primary"><MessageSquarePlus size={12} /><span className="label-caps text-[10px]">Refund updates for client portal</span></div>
                  <div className="grid grid-cols-4 gap-2 items-end">
                    <select className="col-span-1 h-9 rounded-md border border-input bg-background px-2 text-xs"><option>Filed</option><option>Refund sent</option><option>In progress</option></select>
                    <textarea rows={2} placeholder="e.g. 1040 filed today — IRS accepted." className="col-span-3 rounded-md border border-input bg-background p-2 text-xs" />
                  </div>
                  <Button size="sm" className="mt-2" onClick={() => demoAction("post update")}><Send size={12} className="mr-1" /> Post update</Button>
                </div>
                <div className="border-t border-border pt-4">
                  <div className="flex items-center gap-2 mb-3 text-primary"><DollarSign size={12} /><span className="label-caps text-[10px]">Cash advance · Refund Advantage</span></div>
                  <div className="text-xs mb-3">Client's expected refund: <strong>$4,200</strong> · Status: <span className="label-caps text-[9px] bg-secondary px-2 py-1 rounded">pending</span></div>
                  <div className="flex gap-2">
                    <Input placeholder="Amount approved (e.g. 3000)" className="max-w-xs" />
                    <Button size="sm" onClick={() => demoAction("approve advance")}>Approve</Button>
                    <Button size="sm" variant="destructive" onClick={() => demoAction("deny advance")}>Deny</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DemoLayout>
  );
}

// -------- Amendments --------
export function DemoAmendments() {
  const rows = [
    { name: "Priya Nair",   ty: 2023, reason: "Corrected doc",    files: 3, when: "5 hours ago",  status: "new" },
    { name: "Devon Rivera", ty: 2022, reason: "Missing 1099",     files: 2, when: "2 days ago",   status: "new" },
    { name: "Marcus Wu",    ty: 2023, reason: "Missed credit",    files: 4, when: "1 week ago",   status: "completed" },
  ];
  return (
    <DemoLayout>
      <div className="max-w-5xl mx-auto p-6 lg:p-10" data-testid="demo-amendments">
        <PageHeader eyebrow="Amendments · 1040-X" title="Prior-year fixes." sub="Same UI as intakes but for corrections to returns already filed. Flat $385 per amendment." />
        <div className="space-y-2">
          {rows.map((r, i) => (
            <Card key={i} className="p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]"><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">TY{r.ty} · {r.reason} · {r.files} files</div></div>
              <div className="text-xs text-muted-foreground">{r.when}</div>
              <span className={`text-[10px] label-caps px-2 py-1 rounded ${r.status === "completed" ? "bg-primary/15 text-primary" : "bg-secondary"}`}>{r.status === "new" ? "New" : "Filed"}</span>
              <Button size="sm" variant="outline" onClick={() => demoAction("open")}><Eye size={12} className="mr-1" /> View</Button>
            </Card>
          ))}
        </div>
      </div>
    </DemoLayout>
  );
}

// -------- Appointments --------
export function DemoAppointments() {
  const today = new Date();
  const t = (h) => { const d = new Date(today); d.setHours(h, 0, 0, 0); return d; };
  const tomorrow = (h) => { const d = new Date(today); d.setDate(d.getDate() + 1); d.setHours(h, 0, 0, 0); return d; };
  const todaySlots = [
    { time: t(11), name: "Jasmine Carter", service: "Family return", status: "booked" },
    { time: t(14), name: "Marcus Wu",      service: "Simple return", status: "booked" },
  ];
  const upcoming = [
    { time: tomorrow(10), name: "Devon Rivera", service: "Self-employed", status: "booked" },
    { time: tomorrow(13), name: null,           service: null, status: "available" },
    { time: tomorrow(15), name: "Alicia Moreno", service: "Consult",       status: "booked" },
  ];
  const Group = ({ title, list }) => (
    <section>
      <div className="label-caps text-[10px] mb-3">{title} · {list.length}</div>
      <div className="space-y-2">
        {list.map((r, i) => (
          <Card key={i} className="p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[180px]"><div className="text-sm font-medium">{r.time.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div><div className="text-xs text-muted-foreground">30 min</div></div>
            {r.name ? (
              <div className="flex-1 min-w-[220px] text-xs"><div className="flex items-center gap-1 mb-0.5"><User size={11} /><strong>{r.name}</strong></div><div>{r.service}</div></div>
            ) : (
              <div className="flex-1 min-w-[220px] text-xs text-muted-foreground">—</div>
            )}
            <span className={`text-[10px] label-caps px-2 py-1 rounded ${r.status === "booked" ? "bg-accent/15 text-accent" : "bg-primary/15 text-primary"}`}>{r.status === "booked" ? "Booked" : "Open"}</span>
            {r.status === "booked" ? (
              <>
                <Button size="sm" variant="outline" onClick={() => demoAction("mark done")}><Check size={12} className="mr-1" /> Done</Button>
                <Button size="sm" variant="outline" onClick={() => demoAction("no show")}><X size={12} className="mr-1" /> No show</Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={() => demoAction("block")}><Ban size={12} className="mr-1" /> Block</Button>
            )}
          </Card>
        ))}
      </div>
    </section>
  );
  return (
    <DemoLayout>
      <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-8" data-testid="demo-appointments">
        <PageHeader eyebrow="Appointments" title="Bookings & availability." sub="Clients pick times from /book — you see Today / Upcoming / Past. Emails fire on booking + calendar invite (.ics) auto-generates." />
        <Group title="Today" list={todaySlots} />
        <Group title="Upcoming" list={upcoming} />
      </div>
    </DemoLayout>
  );
}

// -------- Weekly digest --------
export function DemoDigest() {
  return (
    <DemoLayout>
      <div className="max-w-3xl mx-auto p-6 lg:p-10" data-testid="demo-digest">
        <PageHeader eyebrow="Weekly digest" title="Your week at a glance." sub="Sent to your inbox every Monday — also viewable here. Great for the co-teacher or spouse in the business." />
        <Card className="p-8 space-y-5">
          <div className="flex items-center gap-2 text-primary"><TrendingUp size={14} /><span className="label-caps text-[10px]">Week of Feb 24 – Mar 2</span></div>
          <h2 className="font-serif text-3xl">Good week, Dimeka.</h2>
          <p className="text-sm">
            You picked up <strong>6 new students</strong>, filed <strong>8 returns</strong>, and closed <strong>$1,890</strong> in revenue.
            Two clients are still in the intake queue waiting on you.
          </p>
          <div className="grid grid-cols-3 gap-4 text-center pt-2">
            <Cell v="8" k="Returns filed" />
            <Cell v="$1,890" k="Revenue" />
            <Cell v="47" k="Students total" />
          </div>
          <div className="border-t border-border pt-4 text-xs text-muted-foreground">
            <div className="font-medium mb-1 text-foreground">Heads up for next week</div>
            <ul className="list-disc pl-4 space-y-1">
              <li>Priya Nair's 1040-X ({"corrected 1099"}) is due Wednesday</li>
              <li>Marcus Wu's Instructor trial ends Thursday</li>
              <li>Two Cash App payments waiting for your approval</li>
            </ul>
          </div>
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={() => demoAction("resend to inbox")}>Email this to me</Button>
          </div>
        </Card>
      </div>
    </DemoLayout>
  );
}

const Field = ({ label, v, mono }) => (
  <div>
    <div className="label-caps text-[9px] mb-1">{label}</div>
    <div className={`text-sm ${mono ? "font-mono text-xs" : ""}`}>{v}</div>
  </div>
);
const Cell = ({ v, k }) => (
  <div>
    <div className="font-serif text-3xl">{v}</div>
    <div className="label-caps text-[9px]">{k}</div>
  </div>
);
